import { TaxProcedure, type VatRateDto } from '@carvia/ros-client-types';
import { normalizeVatId } from '@/utils/vat-id';

export const REGULAR_TAX_PERCENT = 19;

export function canUseReverseChargeTaxProcedure(
  vatId?: string | null,
  options?: { requireVatId?: boolean },
): boolean {
  if (options?.requireVatId === false) {
    return true;
  }
  return !!normalizeVatId(vatId);
}

export function isNonStandardTaxProcedure(taxProcedure: TaxProcedure): boolean {
  return taxProcedure !== TaxProcedure.REGULAR;
}

export function resolveTaxPercentFromTaxProcedure(taxProcedure: TaxProcedure): number {
  return taxProcedure === TaxProcedure.REGULAR ? REGULAR_TAX_PERCENT : 0;
}

export function resolvePricingTaxPercent(
  taxPercent: number | null | undefined,
  taxProcedure: TaxProcedure,
): number {
  if (isNonStandardTaxProcedure(taxProcedure)) {
    return 0;
  }
  if (taxPercent !== null && taxPercent !== undefined) {
    return taxPercent;
  }
  return resolveTaxPercentFromTaxProcedure(taxProcedure);
}

/** Countries with a VAT rate valid at `at`; others fall back to the German rate. */
export function getVatRateCountries(vatRates: VatRateDto[] | undefined, at: Date): string[] {
  const atMs = at.getTime();
  return [
    ...new Set(
      (vatRates ?? [])
        .filter(
          (entry) => (entry.validFrom ? new Date(entry.validFrom).getTime() : -Infinity) <= atMs,
        )
        .map((entry) => entry.country),
    ),
  ];
}

/** Without a country (or a rate for it) the invoice issuer's German rate applies. */
export function getVatPercentForCountry(
  vatRates: VatRateDto[] | undefined,
  country: string | null | undefined,
  at: Date = new Date(),
): number {
  const normalized = country?.toUpperCase() || 'DE';
  const atMs = at.getTime();
  const pick = (lookupCountry: string): number | undefined => {
    if (!vatRates?.length) {
      return undefined;
    }
    for (let i = vatRates.length - 1; i >= 0; i--) {
      const entry = vatRates[i];
      if (entry.country !== lookupCountry) {
        continue;
      }
      const validFrom = entry.validFrom
        ? new Date(entry.validFrom).getTime()
        : Number.NEGATIVE_INFINITY;
      if (validFrom <= atMs) {
        return entry.taxPercent;
      }
    }
    return undefined;
  };
  return pick(normalized) ?? pick('DE') ?? REGULAR_TAX_PERCENT;
}
